"use client";

import { useMotionTemplate, useReducedMotion, useSpring } from "motion/react";
import { type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AUDIO_DRIFT_CHECK_INTERVAL_MS,
  AUDIO_DRIFT_HARD_SYNC_THRESHOLD_SECONDS,
  AUDIO_DRIFT_MAX_PLAYBACK_RATE_DELTA,
  AUDIO_DRIFT_PLAYBACK_RATE_CORRECTION_FACTOR,
  AUDIO_DRIFT_TOLERANCE_SECONDS,
  AUDIO_METADATA_READY_STATE,
  VIDEO_IDLE_BLUR,
  VIDEO_IDLE_OPACITY,
  VIDEO_IDLE_SCALE,
  VIDEO_PRESS_BLUR,
  VIDEO_PRESS_OPACITY,
  VIDEO_PRESS_SCALE,
  VIDEO_PULSE_BLUR_MEDIA_QUERY,
  VIDEO_PULSE_SPRING,
  VIDEO_REBOUND_DELAY_MS,
} from "./constants";

export type DubbedVideoPlayerOptions = {
  initialTrackKey: string;
  onBeforePlay?: () => void;
  preloadTrackKeys?: Iterable<string>;
};

export function useDubbedVideoPlayer({ initialTrackKey, onBeforePlay, preloadTrackKeys }: DubbedVideoPlayerOptions) {
  const [activeTrackKey, setActiveTrackKey] = useState(initialTrackKey);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoBlurEnabled, setIsVideoBlurEnabled] = useState(false);
  const [progressAnimationStartedAt, setProgressAnimationStartedAt] = useState<number | null>(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const reducedMotion = useReducedMotion() ?? false;
  const videoBlur = useSpring(VIDEO_IDLE_BLUR, VIDEO_PULSE_SPRING);
  const videoFilter = useMotionTemplate`blur(${videoBlur}px)`;
  const videoOpacity = useSpring(VIDEO_IDLE_OPACITY, VIDEO_PULSE_SPRING);
  const videoScale = useSpring(VIDEO_IDLE_SCALE, VIDEO_PULSE_SPRING);
  const activeTrackKeyRef = useRef(initialTrackKey);
  const audioDriftCheckedAtRef = useRef(0);
  const audioElements = useRef(new Map<string, HTMLAudioElement>());
  const progressAnimationFrameRef = useRef<number | null>(null);
  const reboundTimeoutRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const warmedAudioTrackKeysRef = useRef(new Set<string>());
  const preloadTrackKeySet = useMemo(() => new Set(preloadTrackKeys ?? []), [preloadTrackKeys]);

  const videoMotionStyle = {
    ...(isVideoBlurEnabled ? { filter: videoFilter } : {}),
    opacity: videoOpacity,
    scale: videoScale,
  };

  const clearVideoPulseTimeout = useCallback(() => {
    if (reboundTimeoutRef.current === null) return;

    window.clearTimeout(reboundTimeoutRef.current);
    reboundTimeoutRef.current = null;
  }, []);

  const pauseAudioTracks = useCallback((exceptTrackKey?: string) => {
    audioElements.current.forEach((audio, trackKey) => {
      if (trackKey === exceptTrackKey) return;

      resetAudioPlaybackRate(audio);
      audio.pause();
    });
  }, []);

  const stopVideoProgressTracking = useCallback(() => {
    if (progressAnimationFrameRef.current === null) return;

    window.cancelAnimationFrame(progressAnimationFrameRef.current);
    progressAnimationFrameRef.current = null;
  }, []);

  const syncVideoProgress = useCallback((element: HTMLVideoElement) => {
    setVideoCurrentTime(element.currentTime);
    setVideoDuration(Number.isFinite(element.duration) ? element.duration : 0);
  }, []);

  const triggerVideoPulse = useCallback(() => {
    if (reducedMotion) return;

    clearVideoPulseTimeout();
    if (isVideoBlurEnabled) videoBlur.stop();
    videoOpacity.stop();
    videoScale.stop();
    if (isVideoBlurEnabled) videoBlur.set(VIDEO_PRESS_BLUR);
    videoOpacity.set(VIDEO_PRESS_OPACITY);
    videoScale.set(VIDEO_PRESS_SCALE);

    reboundTimeoutRef.current = window.setTimeout(() => {
      if (isVideoBlurEnabled) videoBlur.set(VIDEO_IDLE_BLUR);
      videoOpacity.set(VIDEO_IDLE_OPACITY);
      videoScale.set(VIDEO_IDLE_SCALE);
      reboundTimeoutRef.current = null;
    }, VIDEO_REBOUND_DELAY_MS);
  }, [clearVideoPulseTimeout, isVideoBlurEnabled, reducedMotion, videoBlur, videoOpacity, videoScale]);

  const warmAudioTrack = useCallback(
    (trackKey: string) => {
      if (preloadTrackKeySet.has(trackKey)) return;
      if (warmedAudioTrackKeysRef.current.has(trackKey)) return;

      const audioElement = audioElements.current.get(trackKey);
      if (!audioElement) return;

      audioElement.preload = "auto";
      audioElement.load();
      warmedAudioTrackKeysRef.current.add(trackKey);
    },
    [preloadTrackKeySet],
  );

  const playAudioTrack = useCallback(
    (trackKey: string, syncedVideoElement: HTMLVideoElement) => {
      const audioElement = audioElements.current.get(trackKey);
      pauseAudioTracks(trackKey);
      if (!audioElement) return;

      syncAudioToVideo(audioElement, syncedVideoElement);
      audioElement.play().catch(() => {});
    },
    [pauseAudioTracks],
  );

  const rescueActiveAudioDrift = useCallback((syncedVideoElement: HTMLVideoElement, timestamp = performance.now()) => {
    if (timestamp - audioDriftCheckedAtRef.current < AUDIO_DRIFT_CHECK_INTERVAL_MS) return;

    audioDriftCheckedAtRef.current = timestamp;

    const audioElement = audioElements.current.get(activeTrackKeyRef.current);
    if (!audioElement || audioElement.paused || syncedVideoElement.paused) return;
    if (audioElement.readyState < AUDIO_METADATA_READY_STATE) return;

    correctAudioDrift(audioElement, syncedVideoElement);
  }, []);

  const playMediaTrack = useCallback(
    (trackKey: string) => {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      onBeforePlay?.();
      warmAudioTrack(trackKey);

      if (videoElement.ended) {
        videoElement.currentTime = 0;
      }

      if (!videoElement.paused) {
        playAudioTrack(trackKey, videoElement);
      }

      videoElement.play().catch(() => {});
    },
    [onBeforePlay, playAudioTrack, warmAudioTrack],
  );

  const selectTrack = useCallback(
    (trackKey: string) => {
      const wasTrackActive = trackKey === activeTrackKeyRef.current;

      activeTrackKeyRef.current = trackKey;

      if (!wasTrackActive) {
        setActiveTrackKey(trackKey);
        triggerVideoPulse();
      }

      playMediaTrack(trackKey);
    },
    [playMediaTrack, triggerVideoPulse],
  );

  const pausePlayback = useCallback(() => {
    pauseAudioTracks();
    videoRef.current?.pause();
  }, [pauseAudioTracks]);

  const togglePlayback = useCallback(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (videoElement.paused) {
      playMediaTrack(activeTrackKeyRef.current);
      return;
    }

    pausePlayback();
  }, [pausePlayback, playMediaTrack]);

  const startVideoProgressTracking = useCallback(
    (element: HTMLVideoElement) => {
      stopVideoProgressTracking();
      syncVideoProgress(element);

      const updateProgress = (timestamp: number) => {
        syncVideoProgress(element);
        rescueActiveAudioDrift(element, timestamp);

        if (element.paused) {
          progressAnimationFrameRef.current = null;
          return;
        }

        progressAnimationFrameRef.current = window.requestAnimationFrame(updateProgress);
      };

      progressAnimationFrameRef.current = window.requestAnimationFrame(updateProgress);
    },
    [rescueActiveAudioDrift, stopVideoProgressTracking, syncVideoProgress],
  );

  const handleVideoPlay = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      setIsPlaying(true);
      setProgressAnimationStartedAt(startedAt => startedAt ?? performance.now());
      playAudioTrack(activeTrackKeyRef.current, event.currentTarget);
      startVideoProgressTracking(event.currentTarget);
    },
    [playAudioTrack, startVideoProgressTracking],
  );

  const handleVideoPause = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      setIsPlaying(false);
      pauseAudioTracks();
      stopVideoProgressTracking();
      syncVideoProgress(event.currentTarget);
    },
    [pauseAudioTracks, stopVideoProgressTracking, syncVideoProgress],
  );

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    audioElements.current.forEach(audio => {
      resetAudioPlaybackRate(audio);
      audio.pause();
      setAudioCurrentTime(audio, 0);
    });
    stopVideoProgressTracking();
    setVideoCurrentTime(0);
    setProgressAnimationStartedAt(null);
  }, [stopVideoProgressTracking]);

  const handleVideoSeeked = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const audioElement = audioElements.current.get(activeTrackKeyRef.current);

      syncVideoProgress(event.currentTarget);
      if (audioElement) syncAudioToVideo(audioElement, event.currentTarget);
    },
    [syncVideoProgress],
  );

  const handleVideoProgressChange = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      syncVideoProgress(event.currentTarget);
    },
    [syncVideoProgress],
  );

  const handleVideoLoadedData = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      syncVideoProgress(event.currentTarget);
    },
    [syncVideoProgress],
  );

  const registerAudioElement = useCallback((trackKey: string, audioElement: HTMLAudioElement | null) => {
    if (audioElement) {
      audioElements.current.set(trackKey, audioElement);
      return;
    }

    audioElements.current.delete(trackKey);
  }, []);

  useEffect(() => {
    return () => {
      clearVideoPulseTimeout();
      pauseAudioTracks();
      stopVideoProgressTracking();
    };
  }, [clearVideoPulseTimeout, pauseAudioTracks, stopVideoProgressTracking]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(VIDEO_PULSE_BLUR_MEDIA_QUERY);

    function syncVideoBlurPreference() {
      setIsVideoBlurEnabled(mediaQuery.matches);

      if (mediaQuery.matches) return;

      videoBlur.stop();
      videoBlur.set(VIDEO_IDLE_BLUR);
    }

    syncVideoBlurPreference();
    mediaQuery.addEventListener("change", syncVideoBlurPreference);

    return () => mediaQuery.removeEventListener("change", syncVideoBlurPreference);
  }, [videoBlur]);

  return {
    activeTrackKey,
    isPlaying,
    pausePlayback,
    playMediaTrack,
    progressAnimationStartedAt,
    reducedMotion,
    registerAudioElement,
    selectTrack,
    togglePlayback,
    videoCurrentTime,
    videoDuration,
    videoEventHandlers: {
      onDurationChange: handleVideoProgressChange,
      onEnded: handleVideoEnded,
      onLoadedData: handleVideoLoadedData,
      onLoadedMetadata: handleVideoProgressChange,
      onPause: handleVideoPause,
      onPlay: handleVideoPlay,
      onSeeked: handleVideoSeeked,
    },
    videoMotionStyle,
    videoRef,
    warmAudioTrack,
  };
}

function syncAudioToVideo(audioElement: HTMLAudioElement, videoElement: HTMLVideoElement) {
  const videoCurrentTime = videoElement.ended ? 0 : videoElement.currentTime;
  resetAudioPlaybackRate(audioElement);

  if (audioElement.readyState >= AUDIO_METADATA_READY_STATE) {
    setAudioCurrentTime(audioElement, videoCurrentTime);
    return;
  }

  audioElement.addEventListener("loadedmetadata", () => setAudioCurrentTime(audioElement, videoCurrentTime), {
    once: true,
  });
  audioElement.load();
}

function correctAudioDrift(audioElement: HTMLAudioElement, videoElement: HTMLVideoElement) {
  const drift = audioElement.currentTime - videoElement.currentTime;
  const absoluteDrift = Math.abs(drift);

  if (absoluteDrift <= AUDIO_DRIFT_TOLERANCE_SECONDS) {
    resetAudioPlaybackRate(audioElement);
    return;
  }

  if (absoluteDrift >= AUDIO_DRIFT_HARD_SYNC_THRESHOLD_SECONDS) {
    syncAudioToVideo(audioElement, videoElement);
    return;
  }

  const playbackRateDelta = Math.min(
    AUDIO_DRIFT_MAX_PLAYBACK_RATE_DELTA,
    absoluteDrift * AUDIO_DRIFT_PLAYBACK_RATE_CORRECTION_FACTOR,
  );

  audioElement.playbackRate = drift > 0 ? 1 - playbackRateDelta : 1 + playbackRateDelta;
}

function resetAudioPlaybackRate(audioElement: HTMLAudioElement) {
  if (audioElement.playbackRate === 1) return;

  audioElement.playbackRate = 1;
}

function setAudioCurrentTime(audioElement: HTMLAudioElement, currentTime: number) {
  if (audioElement.readyState < AUDIO_METADATA_READY_STATE) return;

  const duration = Number.isFinite(audioElement.duration) ? audioElement.duration : currentTime;
  audioElement.currentTime = Math.min(currentTime, duration);
}
